import React, { useRef, useEffect, useCallback, useState, useMemo } from 'react';
import { pdfToViewportCoords, getDefaultFieldSize, clientRectToPdfBounds } from '../../utils/fieldEditorUtils';

function EditorCanvas({
  canvasRef,
  overlayRef,
  containerRef,
  pdfDoc,
  currentPage,
  scale,
  pdfFields,
  configuration,
  selectedFieldIndex,
  selectedFieldIndices,
  addFieldMode,
  onSelectField,
  onToggleFieldSelection,
  onClearSelection,
  onUpdateFieldBounds,
  onCreateCustomField,
  onSetSelectedFieldIndices,
  onSetSelectedFieldIndex,
  viewportRef,
  onPageChange,
  onCanvasFocusRequest
}) {
  const [selectionBox, setSelectionBox] = useState(null);
  const [alignGuides, setAlignGuides] = useState([]);
  const [editingFieldIndex, setEditingFieldIndex] = useState(null);
  const [testValues, setTestValues] = useState({});
  const [pageViewports, setPageViewports] = useState([]);
  const editInputRef = useRef(null);
  const draggedSinceMouseDownRef = useRef(false);
  const isSelectingRef = useRef(false);
  const selectionStartRef = useRef(null);
  const dragInfoRef = useRef({ isDragging: false, fieldIndex: -1, startX: 0, startY: 0, initialPositions: new Map() });
  const resizeInfoRef = useRef({ isResizing: false, handle: null, fieldIndex: -1, startX: 0, startY: 0, initialBounds: null, selectedFieldsBounds: null });
  const lastMousePosRef = useRef({ x: 0, y: 0, page: currentPage || 1 });
  const lastContainerWidthRef = useRef(0);
  const renderIdRef = useRef(0);
  const activeRenderTasksRef = useRef([]);

  const fieldConfigLookup = useMemo(() => {
    const byId = new Map();
    const byName = new Map();
    if (configuration?.fields) {
      configuration.fields.forEach(f => {
        byId.set(f.id, f);
        if (f.isVisible !== false) byName.set(f.pdfFieldName, f);
      });
    }
    return { byId, byName };
  }, [configuration]);

  const getFieldConfig = useCallback((field) => {
    if (!field) return null;
    return (field.configId && fieldConfigLookup.byId.get(field.configId))
      || fieldConfigLookup.byName.get(field.name)
      || null;
  }, [fieldConfigLookup]);

  const getPageContainerWidth = useCallback(() => {
    const wrapper = containerRef.current;
    if (!wrapper) return Math.max(window.innerWidth - 220 - 320 - 64, 400);
    const width = wrapper.clientWidth - 32;
    return width < 100 ? Math.max(window.innerWidth - 220 - 320 - 64, 400) : width;
  }, [containerRef]);

  const renderAllPages = useCallback(async () => {
    if (!pdfDoc || !containerRef.current) return;

    // Cancel any in-progress render tasks from previous calls
    for (const task of activeRenderTasksRef.current) {
      try { task.cancel(); } catch (_) { /* ignore */ }
    }
    activeRenderTasksRef.current = [];

    renderIdRef.current += 1;
    const thisRenderId = renderIdRef.current;
    const containerWidth = getPageContainerWidth();
    const nextPageViewports = [];

    for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum += 1) {
      const page = await pdfDoc.getPage(pageNum);
      if (thisRenderId !== renderIdRef.current) return;

      // Let pdf.js use the page's built-in rotation (Rotate property in PDF dictionary)
      const viewport1 = page.getViewport({ scale: 1 });
      const baseAutoScale = containerWidth / viewport1.width;
      const actualScale = baseAutoScale * scale;
      const displayViewport = page.getViewport({ scale: actualScale });
      const dpr = Math.min(window.devicePixelRatio || 2, 3);
      const renderViewport = page.getViewport({ scale: actualScale * dpr });

      const pageCanvas = containerRef.current.querySelector(`[data-page-canvas="${pageNum}"]`);
      const pageOverlay = containerRef.current.querySelector(`[data-page-overlay="${pageNum}"]`);
      const pageContainer = containerRef.current.querySelector(`[data-page-container="${pageNum}"]`);

      if (!pageCanvas || !pageOverlay || !pageContainer) {
        console.warn(`[EditorCanvas] Missing DOM for page ${pageNum}`, { pageCanvas: !!pageCanvas, pageOverlay: !!pageOverlay, pageContainer: !!pageContainer });
        continue;
      }

      pageCanvas.width = renderViewport.width;
      pageCanvas.height = renderViewport.height;
      pageCanvas.style.width = `${displayViewport.width}px`;
      pageCanvas.style.height = `${displayViewport.height}px`;
      pageContainer.style.width = `${displayViewport.width}px`;
      pageContainer.style.height = `${displayViewport.height}px`;
      pageOverlay.style.width = `${displayViewport.width}px`;
      pageOverlay.style.height = `${displayViewport.height}px`;

      const ctx = pageCanvas.getContext('2d');
      try {
        const renderTask = page.render({ canvasContext: ctx, viewport: renderViewport });
        activeRenderTasksRef.current.push(renderTask);
        await renderTask.promise;
      } catch (err) {
        if (err?.name !== 'RenderingCancelledException') {
          console.error(`[EditorCanvas] Render error page ${pageNum}:`, err);
        }
      }

      if (thisRenderId !== renderIdRef.current) return;
      nextPageViewports.push({ pageNum, viewport: displayViewport, scale: actualScale });
    }

    activeRenderTasksRef.current = [];
    setPageViewports(nextPageViewports);
    if (viewportRef) {
      viewportRef.current = nextPageViewports;
    }
  }, [pdfDoc, scale, containerRef, viewportRef, getPageContainerWidth]);

  useEffect(() => {
    // Use double-rAF to ensure DOM is committed before rendering
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        renderAllPages();
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [renderAllPages]);

  useEffect(() => {
    if (!containerRef.current) return;
    let resizeRafId = null;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const newWidth = entry.contentRect.width;
        if (Math.abs(newWidth - lastContainerWidthRef.current) > 1) {
          lastContainerWidthRef.current = newWidth;
          if (resizeRafId) cancelAnimationFrame(resizeRafId);
          resizeRafId = requestAnimationFrame(() => {
            renderAllPages();
          });
        }
      }
    });
    observer.observe(containerRef.current);
    return () => {
      observer.disconnect();
      if (resizeRafId) cancelAnimationFrame(resizeRafId);
    };
  }, [renderAllPages, containerRef]);

  useEffect(() => {
    if (editingFieldIndex !== null && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingFieldIndex]);

  const getViewportForPage = useCallback((pageNum) => {
    return pageViewports.find(p => p.pageNum === pageNum)?.viewport || null;
  }, [pageViewports]);

  const getOverlayForPage = useCallback((pageNum) => {
    return containerRef.current?.querySelector(`[data-page-overlay="${pageNum}"]`) || null;
  }, [containerRef]);

  const findPageAtClientY = useCallback((clientY) => {
    const overlays = containerRef.current?.querySelectorAll('[data-page-overlay]') || [];
    let best = null;
    overlays.forEach((overlay) => {
      const rect = overlay.getBoundingClientRect();
      const pageNum = Number(overlay.dataset.pageNumber);
      if (clientY >= rect.top && clientY <= rect.bottom) {
        best = { pageNum, overlay, rect };
      }
    });
    if (best) return best;
    let nearest = null;
    let nearestDist = Infinity;
    overlays.forEach((overlay) => {
      const rect = overlay.getBoundingClientRect();
      const pageNum = Number(overlay.dataset.pageNumber);
      const dist = clientY < rect.top ? rect.top - clientY : clientY - rect.bottom;
      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = { pageNum, overlay, rect };
      }
    });
    return nearest;
  }, [containerRef]);

  const getFieldPosition = useCallback((field) => {
    if (!field?.bounds) return null;
    const fieldConfig = getFieldConfig(field);
    if (fieldConfig && fieldConfig.isVisible === false) return null;
    const bounds = fieldConfig?.bounds || field.bounds;
    const viewport = getViewportForPage(field.page || 1);
    if (!viewport) return null;
    return pdfToViewportCoords(viewport, bounds);
  }, [getFieldConfig, getViewportForPage]);

  const pageFieldsWithIndex = useMemo(() => {
    const grouped = new Map();
    pdfFields.forEach((field, index) => {
      const pageNum = field.page || 1;
      if (!grouped.has(pageNum)) grouped.set(pageNum, []);
      grouped.get(pageNum).push({ field, globalIndex: index });
    });
    return grouped;
  }, [pdfFields]);

  const HANDLE_POSITIONS = ['nw', 'n', 'ne', 'w', 'e', 'sw', 's', 'se'];

  const handleFieldClick = useCallback((e, fieldIndex) => {
    if (addFieldMode) return;
    if (e.target.classList.contains('resize-handle')) return;
    if (e.target.tagName === 'INPUT') return;
    // Ctrl+drag must not also toggle selection when the mouse is released.
    if (draggedSinceMouseDownRef.current) {
      draggedSinceMouseDownRef.current = false;
      return;
    }
    e.stopPropagation();
    onCanvasFocusRequest?.();
    if (e.ctrlKey || e.metaKey) onToggleFieldSelection(fieldIndex);
    else onSelectField(fieldIndex);
  }, [addFieldMode, onCanvasFocusRequest, onSelectField, onToggleFieldSelection]);

  const handleFieldDoubleClick = useCallback((e, fieldIndex) => {
    if (addFieldMode) return;
    if (e.target.classList.contains('resize-handle')) return;
    e.stopPropagation();
    e.preventDefault();
    onCanvasFocusRequest?.();
    setEditingFieldIndex(fieldIndex);
    onSelectField(fieldIndex);
  }, [addFieldMode, onCanvasFocusRequest, onSelectField]);

  const handleOverlayClick = useCallback(async (e, pageNum) => {
    onCanvasFocusRequest?.();
    if (!addFieldMode) {
      if (editingFieldIndex !== null && !e.target.closest('.field-test-input') && !e.target.closest('.field-highlight')) {
        setEditingFieldIndex(null);
      }
      return;
    }

    if (e.target.classList.contains('field-highlight') || e.target.classList.contains('resize-handle') || e.target.closest('.field-highlight')) return;

    const overlay = getOverlayForPage(pageNum);
    if (!overlay) return;
    const rect = overlay.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // getDefaultFieldSize returns PDF points, but x/y above are screen pixels and
    // createCustomField converts everything it is given through convertToPdfPoint.
    // Passing the points straight through divided them by the zoom a second time, so a
    // field was stored ~40% smaller than it looked and its top edge landed in the wrong
    // place. The mismatch was invisible until reload, because the canvas kept showing
    // the freshly created field at the size it was drawn.
    const defaults = getDefaultFieldSize(addFieldMode);
    const pageScale = getViewportForPage(pageNum)?.scale || 1;
    const width = defaults.width * pageScale;
    const height = defaults.height * pageScale;

    await onCreateCustomField(x, y, width, height, addFieldMode, pageNum);
  }, [addFieldMode, editingFieldIndex, getOverlayForPage, getViewportForPage, onCanvasFocusRequest, onCreateCustomField]);

  const handleFieldMouseDown = useCallback((e, fieldIndex, pageNum) => {
    if (e.target.classList.contains('resize-handle')) return;
    if (addFieldMode) return;
    onCanvasFocusRequest?.();

    const overlay = getOverlayForPage(pageNum);
    if (!overlay) return;

    draggedSinceMouseDownRef.current = false;
    const info = dragInfoRef.current;
    info.isDragging = true;
    info.fieldIndex = fieldIndex;
    info.startX = e.clientX;
    info.startY = e.clientY;
    info.initialPositions.clear();

    const indicesToMove = selectedFieldIndices.has(fieldIndex) ? [...selectedFieldIndices] : [fieldIndex];
    indicesToMove.forEach(idx => {
      const el = containerRef.current?.querySelector(`[data-field-index="${idx}"]`);
      if (el) {
        info.initialPositions.set(idx, {
          left: el.offsetLeft,
          top: el.offsetTop,
          page: Number(el.dataset.pageNumber || pageNum)
        });
      }
    });
    e.preventDefault();
  }, [addFieldMode, onCanvasFocusRequest, selectedFieldIndices, getOverlayForPage, containerRef]);

  const handleResizeMouseDown = useCallback((e, fieldIndex, handlePos, pageNum) => {
    e.stopPropagation();
    e.preventDefault();
    onCanvasFocusRequest?.();
    const el = containerRef.current?.querySelector(`[data-field-index="${fieldIndex}"]`);
    if (!el) return;
    const info = resizeInfoRef.current;
    info.isResizing = true;
    info.handle = handlePos;
    info.fieldIndex = fieldIndex;
    info.startX = e.clientX;
    info.startY = e.clientY;
    info.initialBounds = {
      width: el.offsetWidth,
      height: el.offsetHeight,
      left: el.offsetLeft,
      top: el.offsetTop,
      page: pageNum
    };
    // Store initial bounds for all selected fields (for multi-resize)
    const allBounds = new Map();
    const allSelected = new Set(selectedFieldIndices);
    if (selectedFieldIndex !== null) allSelected.add(selectedFieldIndex);
    allSelected.add(fieldIndex);
    allSelected.forEach(idx => {
      const fel = containerRef.current?.querySelector(`[data-field-index="${idx}"]`);
      if (fel) {
        allBounds.set(idx, { width: fel.offsetWidth, height: fel.offsetHeight, left: fel.offsetLeft, top: fel.offsetTop });
      }
    });
    info.selectedFieldsBounds = allBounds;
  }, [containerRef, onCanvasFocusRequest, selectedFieldIndices, selectedFieldIndex]);

  const handleOverlayMouseDown = useCallback((e, pageNum) => {
    if (!e.currentTarget || e.target !== e.currentTarget) return;
    if (addFieldMode) return;
    onCanvasFocusRequest?.();

    isSelectingRef.current = true;
    const rect = e.currentTarget.getBoundingClientRect();
    selectionStartRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      page: pageNum
    };
    setSelectionBox({
      left: e.clientX - rect.left,
      top: e.clientY - rect.top,
      width: 0,
      height: 0,
      page: pageNum
    });
    e.preventDefault();
  }, [addFieldMode, onCanvasFocusRequest]);

  const handleOverlayMouseMove = useCallback((e, pageNum) => {
    const rect = e.currentTarget.getBoundingClientRect();
    lastMousePosRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top, page: pageNum };
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      const drag = dragInfoRef.current;
      if (drag.isDragging) {
        const dx = e.clientX - drag.startX;
        const dy = e.clientY - drag.startY;
        if (Math.abs(dx) > 2 || Math.abs(dy) > 2) draggedSinceMouseDownRef.current = true;
        // Re-read Ctrl on each move so releasing it restores snapping mid-drag.
        const SNAP_THRESHOLD = e.ctrlKey ? 0 : 5;
        const guides = [];

        // Use the wrapper as the common coordinate system for cross-page alignment
        const wrapperEl = containerRef.current?.querySelector('.pdf-canvas-wrapper');
        const wrapperRect = wrapperEl?.getBoundingClientRect();
        if (!wrapperRect) return;

        // Get all other fields' positions relative to the wrapper
        const otherFieldEdges = [];
        pdfFields.forEach((field, idx) => {
          if (drag.initialPositions.has(idx)) return; // skip dragged fields
          const el = containerRef.current?.querySelector(`[data-field-index="${idx}"]`);
          if (!el) return;
          const r = el.getBoundingClientRect();
          const l = r.left - wrapperRect.left, t = r.top - wrapperRect.top, w = r.width, h = r.height;
          otherFieldEdges.push({ left: l, right: l + w, top: t, bottom: t + h, cx: l + w / 2, cy: t + h / 2 });
        });

        // Compute the primary dragged field's new position relative to the wrapper
        const primaryIdx = drag.initialPositions.keys().next().value;
        const primaryPos = drag.initialPositions.get(primaryIdx);
        if (primaryPos) {
          const primaryEl = containerRef.current?.querySelector(`[data-field-index="${primaryIdx}"]`);
          if (!primaryEl) return;
          const primaryRect = primaryEl.getBoundingClientRect();
          const pw = primaryRect.width, ph = primaryRect.height;
          // Current position relative to wrapper (already includes dx/dy from DOM updates)
          const newLeft = primaryPos.left + dx;
          const newTop = primaryPos.top + dy;
          // Convert to wrapper-relative coords
          const overlayRect = primaryEl.offsetParent?.getBoundingClientRect();
          const overlayOffX = overlayRect ? overlayRect.left - wrapperRect.left : 0;
          const overlayOffY = overlayRect ? overlayRect.top - wrapperRect.top : 0;
          const dragEdges = {
            left: newLeft + overlayOffX, right: newLeft + overlayOffX + pw,
            top: newTop + overlayOffY, bottom: newTop + overlayOffY + ph,
            cx: newLeft + overlayOffX + pw / 2, cy: newTop + overlayOffY + ph / 2
          };

          let snapDx = 0, snapDy = 0;
          let bestV = null, bestVDist = SNAP_THRESHOLD;
          let bestH = null, bestHDist = SNAP_THRESHOLD;
          for (const other of otherFieldEdges) {
            // Vertical alignment (left, right, center-x)
            for (const [dragEdge, otherEdge] of [
              [dragEdges.left, other.left], [dragEdges.left, other.right],
              [dragEdges.right, other.left], [dragEdges.right, other.right],
              [dragEdges.cx, other.cx]
            ]) {
              const dist = Math.abs(dragEdge - otherEdge);
              if (dist < bestVDist) {
                bestVDist = dist;
                bestV = { type: 'v', pos: otherEdge, snap: otherEdge - dragEdge };
              }
            }
            // Horizontal alignment (top, bottom, center-y)
            for (const [dragEdge, otherEdge] of [
              [dragEdges.top, other.top], [dragEdges.top, other.bottom],
              [dragEdges.bottom, other.top], [dragEdges.bottom, other.bottom],
              [dragEdges.cy, other.cy]
            ]) {
              const dist = Math.abs(dragEdge - otherEdge);
              if (dist < bestHDist) {
                bestHDist = dist;
                bestH = { type: 'h', pos: otherEdge, snap: otherEdge - dragEdge };
              }
            }
          }
          if (bestV) { guides.push(bestV); snapDx = bestV.snap; }
          if (bestH) { guides.push(bestH); snapDy = bestH.snap; }

          // Apply snapped positions
          drag.initialPositions.forEach((pos, idx) => {
            const el = containerRef.current?.querySelector(`[data-field-index="${idx}"]`);
            if (el) {
              el.style.left = `${pos.left + dx + snapDx}px`;
              el.style.top = `${pos.top + dy + snapDy}px`;
            }
          });
        }

        setAlignGuides(guides);
        return;
      }

      const resize = resizeInfoRef.current;
      if (resize.isResizing && resize.initialBounds) {
        let dx = e.clientX - resize.startX;
        let dy = e.clientY - resize.startY;
        // Re-read Ctrl on each move so releasing it restores snapping mid-drag.
        const SNAP_THRESHOLD = e.ctrlKey ? 0 : 5;
        const guides = [];

        // Compute the primary field's new edges for snap detection
        const wrapperEl = containerRef.current?.querySelector('.pdf-canvas-wrapper');
        const wrapperRect = wrapperEl?.getBoundingClientRect();
        const primaryEl = containerRef.current?.querySelector(`[data-field-index="${resize.fieldIndex}"]`);
        if (wrapperRect && primaryEl) {
          const pb = resize.initialBounds;
          let pw = pb.width, ph = pb.height, pl = pb.left, pt = pb.top;
          if (resize.handle.includes('e')) pw = Math.max(20, pw + dx);
          else if (resize.handle.includes('w')) { pw = Math.max(20, pw - dx); pl = pb.left + (pb.width - pw); }
          if (resize.handle.includes('s')) ph = Math.max(20, ph + dy);
          else if (resize.handle.includes('n')) { ph = Math.max(20, ph - dy); pt = pb.top + (pb.height - ph); }

          // Get wrapper-relative position of the primary field's overlay
          const overlayRect = primaryEl.offsetParent?.getBoundingClientRect();
          const offX = overlayRect ? overlayRect.left - wrapperRect.left : 0;
          const offY = overlayRect ? overlayRect.top - wrapperRect.top : 0;
          const resizeEdges = { left: pl + offX, right: pl + pw + offX, top: pt + offY, bottom: pt + ph + offY };

          // Get other fields' edges (excluding resized fields)
          const boundsMap = resize.selectedFieldsBounds || new Map([[resize.fieldIndex, resize.initialBounds]]);
          const otherEdges = [];
          pdfFields.forEach((field, idx) => {
            if (boundsMap.has(idx)) return;
            const el = containerRef.current?.querySelector(`[data-field-index="${idx}"]`);
            if (!el) return;
            const r = el.getBoundingClientRect();
            const l = r.left - wrapperRect.left, t = r.top - wrapperRect.top;
            otherEdges.push({ left: l, right: l + r.width, top: t, bottom: t + r.height, cx: l + r.width / 2, cy: t + r.height / 2 });
          });

          let bestV = null, bestVDist = SNAP_THRESHOLD;
          let bestH = null, bestHDist = SNAP_THRESHOLD;
          // Only snap the edges being resized
          const vEdgesToCheck = [];
          if (resize.handle.includes('e')) vEdgesToCheck.push(resizeEdges.right);
          if (resize.handle.includes('w')) vEdgesToCheck.push(resizeEdges.left);
          const hEdgesToCheck = [];
          if (resize.handle.includes('s')) hEdgesToCheck.push(resizeEdges.bottom);
          if (resize.handle.includes('n')) hEdgesToCheck.push(resizeEdges.top);

          for (const other of otherEdges) {
            for (const dragEdge of vEdgesToCheck) {
              for (const otherEdge of [other.left, other.right, other.cx]) {
                const dist = Math.abs(dragEdge - otherEdge);
                if (dist < bestVDist) { bestVDist = dist; bestV = { type: 'v', pos: otherEdge, snap: otherEdge - dragEdge }; }
              }
            }
            for (const dragEdge of hEdgesToCheck) {
              for (const otherEdge of [other.top, other.bottom, other.cy]) {
                const dist = Math.abs(dragEdge - otherEdge);
                if (dist < bestHDist) { bestHDist = dist; bestH = { type: 'h', pos: otherEdge, snap: otherEdge - dragEdge }; }
              }
            }
          }
          if (bestV) { guides.push(bestV); if (resize.handle.includes('e')) dx += bestV.snap; else if (resize.handle.includes('w')) dx -= bestV.snap; }
          if (bestH) { guides.push(bestH); if (resize.handle.includes('s')) dy += bestH.snap; else if (resize.handle.includes('n')) dy -= bestH.snap; }
        }

        // Apply resize to all selected fields with snapped dx/dy
        const boundsMap = resize.selectedFieldsBounds || new Map([[resize.fieldIndex, resize.initialBounds]]);
        boundsMap.forEach((bounds, idx) => {
          const fel = containerRef.current?.querySelector(`[data-field-index="${idx}"]`);
          if (!fel) return;
          let { width, height, left, top } = bounds;
          if (resize.handle.includes('e')) width = Math.max(20, width + dx);
          else if (resize.handle.includes('w')) { width = Math.max(20, width - dx); left = bounds.left + (bounds.width - width); }
          if (resize.handle.includes('s')) height = Math.max(20, height + dy);
          else if (resize.handle.includes('n')) { height = Math.max(20, height - dy); top = bounds.top + (bounds.height - height); }
          fel.style.width = `${width}px`;
          fel.style.height = `${height}px`;
          fel.style.left = `${left}px`;
          fel.style.top = `${top}px`;
        });
        setAlignGuides(guides);
        return;
      }

      if (isSelectingRef.current && selectionStartRef.current) {
        const overlay = getOverlayForPage(selectionStartRef.current.page);
        if (!overlay) return;
        const rect = overlay.getBoundingClientRect();
        const currentX = e.clientX - rect.left;
        const currentY = e.clientY - rect.top;
        setSelectionBox({
          left: Math.min(selectionStartRef.current.x, currentX),
          top: Math.min(selectionStartRef.current.y, currentY),
          width: Math.abs(currentX - selectionStartRef.current.x),
          height: Math.abs(currentY - selectionStartRef.current.y),
          page: selectionStartRef.current.page
        });
      }
    };

    const handleMouseUp = (e) => {
      setAlignGuides([]);

      const drag = dragInfoRef.current;
      if (drag.isDragging) {
        drag.isDragging = false;
        const target = findPageAtClientY(e.clientY);
        for (const [fieldIndex] of drag.initialPositions) {
          const el = containerRef.current?.querySelector(`[data-field-index="${fieldIndex}"]`);
          if (!el) continue;
          const pageHit = target || { pageNum: Number(el.dataset.pageNumber || 1), overlay: getOverlayForPage(Number(el.dataset.pageNumber || 1)) };
          const overlay = pageHit.overlay || getOverlayForPage(pageHit.pageNum);
          const viewport = getViewportForPage(pageHit.pageNum);
          if (!overlay || !viewport) continue;
          const bounds = clientRectToPdfBounds(viewport, overlay.getBoundingClientRect(), el.getBoundingClientRect());
          onUpdateFieldBounds(fieldIndex, bounds, pageHit.pageNum);
        }
        drag.initialPositions.clear();
        return;
      }

      const resize = resizeInfoRef.current;
      if (resize.isResizing) {
        // Save bounds for all resized fields
        const boundsMap = resize.selectedFieldsBounds || new Map([[resize.fieldIndex, resize.initialBounds]]);
        boundsMap.forEach((_, idx) => {
          const field = pdfFields[idx];
          const el = containerRef.current?.querySelector(`[data-field-index="${idx}"]`);
          const viewport = getViewportForPage(field?.page || 1);
          if (el && viewport) {
            const overlay = getOverlayForPage(field?.page || 1);
            if (!overlay) return;
            onUpdateFieldBounds(idx, clientRectToPdfBounds(viewport, overlay.getBoundingClientRect(), el.getBoundingClientRect()));
          }
        });
        resize.isResizing = false;
        resize.handle = null;
        resize.initialBounds = null;
        resize.selectedFieldsBounds = null;
        return;
      }

      if (isSelectingRef.current) {
        isSelectingRef.current = false;
        if (selectionBox) {
          // Small pointer jitter is a blank click; only a real drag selects a region.
          const MARQUEE_MIN_PX = 4;
          const isDrag = selectionBox.width >= MARQUEE_MIN_PX || selectionBox.height >= MARQUEE_MIN_PX;

          const overlay = isDrag ? getOverlayForPage(selectionBox.page) : null;
          if (overlay) {
            const overlayRect = overlay.getBoundingClientRect();
            const boxLeft = overlayRect.left + selectionBox.left;
            const boxTop = overlayRect.top + selectionBox.top;
            const boxRight = boxLeft + selectionBox.width;
            const boxBottom = boxTop + selectionBox.height;
            const newIndices = new Set();
            overlay.querySelectorAll('.field-highlight').forEach(el => {
              const fieldRect = el.getBoundingClientRect();
              if (!(fieldRect.right < boxLeft || fieldRect.left > boxRight || fieldRect.bottom < boxTop || fieldRect.top > boxBottom)) {
                newIndices.add(parseInt(el.dataset.fieldIndex, 10));
              }
            });
            onSetSelectedFieldIndices(newIndices);
            onSetSelectedFieldIndex(null);
          } else if (!isDrag) {
            onClearSelection();
            setEditingFieldIndex(null);
          }
        }
        setSelectionBox(null);
        selectionStartRef.current = null;
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [containerRef, findPageAtClientY, getOverlayForPage, getViewportForPage, onSetSelectedFieldIndex, onSetSelectedFieldIndices, onClearSelection, onUpdateFieldBounds, pdfFields, selectionBox]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current._getLastMousePos = () => lastMousePosRef.current;
    }
  }, [containerRef]);

  // IntersectionObserver to detect which page is most visible and update currentPage
  useEffect(() => {
    if (!containerRef.current || !pdfDoc || !onPageChange || pageViewports.length === 0) return;
    const wrapper = containerRef.current;
    const pageBlocks = wrapper.querySelectorAll('[data-page-block]');
    if (pageBlocks.length === 0) return;

    const visibilityMap = new Map();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          const pageNum = parseInt(entry.target.getAttribute('data-page-block'), 10);
          visibilityMap.set(pageNum, entry.intersectionRatio);
        });

        // Find the page with the highest intersection ratio
        let bestPage = 1;
        let bestRatio = 0;
        visibilityMap.forEach((ratio, pageNum) => {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestPage = pageNum;
          }
        });

        onPageChange(bestPage);
      },
      {
        root: wrapper,
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0]
      }
    );

    pageBlocks.forEach(block => observer.observe(block));

    return () => observer.disconnect();
  }, [containerRef, pdfDoc, onPageChange, pageViewports]);

  // Keyed on the selected field's page, not on pdfFields: depending on the array made
  // every field mutation (nudge, paste, resize) re-run this and yank the view back to
  // whichever page the selected field lived on.
  const selectedFieldPage = selectedFieldIndex !== null
    ? (pdfFields[selectedFieldIndex]?.page || 1)
    : null;
  useEffect(() => {
    if (selectedFieldIndex === null || selectedFieldPage === null) return;
    const pageContainer = containerRef.current?.querySelector(`[data-page-block="${selectedFieldPage}"]`);
    pageContainer?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selectedFieldIndex, selectedFieldPage, containerRef]);

  return (
    <div className="editor-canvas-area" ref={containerRef}
      onMouseDown={(e) => {
        if (addFieldMode || e.target.closest('.field-overlay')) return;
        onClearSelection();
        setEditingFieldIndex(null);
      }}
    >
      <div className="pdf-canvas-wrapper multi-page">
        {[...Array(pdfDoc?.numPages || 0)].map((_, index) => {
          const pageNum = index + 1;
          const pageFields = pageFieldsWithIndex.get(pageNum) || [];
          return (
            <div key={pageNum} className="pdf-page-block" data-page-block={pageNum}>
              <div className="pdf-page-label">Page {pageNum}</div>
              <div className="pdf-canvas-container" data-page-container={pageNum}>
                <canvas ref={pageNum === currentPage ? canvasRef : null} data-page-canvas={pageNum} />
                <div
                  className="field-overlay"
                  ref={pageNum === currentPage ? overlayRef : null}
                  data-page-overlay={pageNum}
                  data-page-number={pageNum}
                  tabIndex={0}
                  style={{ cursor: addFieldMode ? 'crosshair' : 'default' }}
                  onClick={(e) => handleOverlayClick(e, pageNum)}
                  onMouseDown={(e) => handleOverlayMouseDown(e, pageNum)}
                  onMouseMove={(e) => handleOverlayMouseMove(e, pageNum)}
                >
                  {pageFields.map(({ field, globalIndex }) => {
                    const pos = getFieldPosition(field);
                    if (!pos) return null;

                    const fieldConfig = getFieldConfig(field);
                    const isCustom = fieldConfig?.isCustomField || field.name.startsWith('custom_');
                    const isSelected = selectedFieldIndex === globalIndex;
                    const isMultiSelected = selectedFieldIndices.has(globalIndex);
                    const isEditing = editingFieldIndex === globalIndex;
                    const hasTestValue = testValues[globalIndex] && testValues[globalIndex].length > 0;
                    const fieldType = fieldConfig?.displayType || field.type;

                    let className = 'field-highlight';
                    if (isCustom) className += ' custom-field';
                    if (fieldType === 'text') className += ' text-field';
                    if (fieldType === 'checkbox') className += ' checkbox-field';
                    if (fieldType === 'radio') className += ' radio-field';
                    if (fieldType === 'date') className += ' date-field';
                    if (fieldType === 'signature') className += ' signature-field';
                    if (fieldType === 'number') className += ' number-field';
                    if (fieldType === 'dropdown') className += ' dropdown-field';
                    if (isSelected) className += ' selected';
                    if (isMultiSelected) className += ' multi-selected';
                    if (isEditing) className += ' editing';

                    const fFamily = fieldConfig?.fontFamily || "'Times New Roman', Times, serif";
                    const fSize = fieldConfig?.fontSize ? Math.min(fieldConfig.fontSize, pos.height * 0.85) + 'px' : Math.min(pos.height * 0.7, 14) + 'px';
                    const fColor = fieldConfig?.fontColor || '#333';
                    const fWeight = fieldConfig?.bold ? 'bold' : 'normal';
                    const fStyle = fieldConfig?.italic ? 'italic' : 'normal';
                    const fDecoration = fieldConfig?.underline ? 'underline' : 'none';
                    const fAlign = fieldConfig?.textAlign || 'left';

                    return (
                      <div
                        key={`${field.name}-${globalIndex}`}
                        className={className}
                        data-field-index={globalIndex}
                        data-page-number={pageNum}
                        tabIndex={isSelected || isMultiSelected ? 0 : -1}
                        style={{ left: `${pos.x}px`, top: `${pos.y}px`, width: `${pos.width}px`, height: `${pos.height}px` }}
                        onClick={(e) => handleFieldClick(e, globalIndex)}
                        onDoubleClick={(e) => handleFieldDoubleClick(e, globalIndex)}
                        onMouseDown={(e) => {
                          if (isEditing && e.target.tagName === 'INPUT') return;
                          handleFieldMouseDown(e, globalIndex, pageNum);
                        }}
                      >
                        {isEditing ? (
                          <input
                            ref={editInputRef}
                            className="field-test-input"
                            type="text"
                            value={testValues[globalIndex] || ''}
                            placeholder={fieldConfig?.label || field.name}
                            onChange={(e) => {
                              e.stopPropagation();
                              setTestValues(prev => ({ ...prev, [globalIndex]: e.target.value }));
                            }}
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                              e.stopPropagation();
                              if (e.key === 'Escape' || e.key === 'Enter') {
                                setEditingFieldIndex(null);
                              }
                            }}
                            style={{ width: '100%', height: '100%', border: 'none', outline: 'none', background: 'transparent', fontSize: fSize, padding: '0 2px', boxSizing: 'border-box', color: fColor, fontFamily: fFamily, fontWeight: fWeight, fontStyle: fStyle, textDecoration: fDecoration, textAlign: fAlign, position: 'relative', zIndex: 1 }}
                          />
                        ) : hasTestValue ? (
                          <div
                            className="field-test-preview"
                            style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: fAlign === 'center' ? 'center' : fAlign === 'right' ? 'flex-end' : 'flex-start', padding: '0 2px', fontSize: fSize, color: fColor, fontFamily: fFamily, fontWeight: fWeight, fontStyle: fStyle, textDecoration: fDecoration, textAlign: fAlign, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', pointerEvents: 'none', background: 'transparent', position: 'relative', zIndex: 1 }}
                          >
                            {testValues[globalIndex]}
                          </div>
                        ) : null}

                        {(isSelected || isMultiSelected) && HANDLE_POSITIONS.map(hp => (
                          <div
                            key={hp}
                            className={`resize-handle ${hp}`}
                            data-position={hp}
                            onMouseDown={(e) => handleResizeMouseDown(e, globalIndex, hp, pageNum)}
                          />
                        ))}
                      </div>
                    );
                  })}

                  {selectionBox?.page === pageNum && (
                    <div
                      className="selection-box"
                      style={{ left: `${selectionBox.left}px`, top: `${selectionBox.top}px`, width: `${selectionBox.width}px`, height: `${selectionBox.height}px` }}
                    />
                  )}

                </div>
              </div>
            </div>
          );
        })}
        {alignGuides.map((guide, i) => (
          <div
            key={`guide-${guide.type}-${i}`}
            className={`align-guide align-guide-${guide.type}`}
            style={guide.type === 'v' ? { left: `${Math.round(guide.pos)}px`, top: 0, width: '1px', height: '100%' } : { top: `${Math.round(guide.pos)}px`, left: 0, height: '1px', width: '100%' }}
          />
        ))}
      </div>
    </div>
  );
}

export default React.memo(EditorCanvas);
